import _ from 'lodash';

import i18n from '@configs/i18n';
import * as routes from '@configs/routes.json';

import { ErrorDetails } from '@utils';
import { get, post } from '@services/method';
import {
  IUpload,
  UploadStatus,
  IExperimentResult,
  ITagPlate,
  ITagPlateSample,
  FlowcellControl,
  Experiment,
  TagPlateNonMrd,
} from '@models/upload';
import { BrandConfig, store } from '@store';

export interface IRunFolderValidationDto {
  experimentList?: File;
  runInfo?: File;
  path: string;
  files: { p: string; s: number }[];
  fullValidation: boolean;
}

enum UploadResultState {
  Pending = 'Pending',
  Claimed = 'Claimed',
  Uploading = 'Uploading',
  Cancelled = 'Cancelled',
  Interrupted = 'Interrupted',
  Failed = 'Failed',
  Completed = 'Completed',
}

enum UploadFailureCause {
  UploadTimedOut = 'UploadTimedOut',
  UploadIllegalState = 'UploadIllegalState',
  UploaderQuit = 'UploaderQuit',
  UploaderSignedOut = 'UploaderSignedOut',
  UploaderHeartbeatsLostUnclaimed = 'UploaderHeartbeatsLostUnclaimed',
  UploaderHeartbeatsLostClaimed = 'UploaderHeartbeatsLostClaimed',
}

const translateUploadResultState = (state: UploadResultState): UploadStatus => {
  switch (state) {
    case UploadResultState.Pending:
      return UploadStatus.uploadPending;
    case UploadResultState.Uploading:
      return UploadStatus.uploading;
    case UploadResultState.Cancelled:
      return UploadStatus.uploadCancelled;
    case UploadResultState.Interrupted:
      return UploadStatus.uploadInterrupted;
    case UploadResultState.Failed:
      return UploadStatus.uploadFailed;
    case UploadResultState.Completed:
      return UploadStatus.uploaded;
    case UploadResultState.Claimed:
      return UploadStatus.uploadClaimed;
  }
};

interface IUploadDto {
  id: string;
  flowCellName: string;
  runFolderPath: string;
  status: string;
  progress: number;
  createdLocalDateTime: number;
  createdUTC: number;
  owner: string;
  experimentListName: string;
  message?: string;
  experimentJson: string | null;
  validationErrorJson: string | null;
  transmitStartedUTC: string;
  transmitEndedUTC: string;
  isInFinalStage: boolean;
}

interface IUploadDtoErrorJson {
  isValid: boolean;
  missingArtifacts?: string[];
  uploadFailureCause?: UploadFailureCause;
}

const parseErrors = (json: string | null): string | null => {
  if (!json) {
    return null;
  }

  const parsed: IUploadDtoErrorJson = JSON.parse(json);
  if (parsed.missingArtifacts) {
    return i18n.t('upload_error_missingFiles', {
      files: parsed.missingArtifacts.join(', '),
    });
  } else {
    switch (parsed.uploadFailureCause) {
      case UploadFailureCause.UploadTimedOut:
        return i18n.t('upload_error_uploadTimedOut');
      case UploadFailureCause.UploadIllegalState:
        return i18n.t('upload_error_uploadIllegalState');
      case UploadFailureCause.UploaderQuit:
        return i18n.t('upload_error_uploaderQuit');
      case UploadFailureCause.UploaderSignedOut:
        return i18n.t('upload_error_uploaderSignedOut');
      case UploadFailureCause.UploaderHeartbeatsLostUnclaimed:
        return i18n.t('upload_error_uploaderHeartbeatsLostUnclaimed');
      case UploadFailureCause.UploaderHeartbeatsLostClaimed:
        return i18n.t('upload_error_uploaderHeartbeatsLostClaimed');
    }
  }
  return null;
};

export const uploads = async (): Promise<IUpload[]> => {
  return get(routes.api.upload.get)
    .then((response: Response) => response.json())
    .then((uploads: IUploadDto[]) => {
      return uploads.map(
        (upload) =>
          ({
            id: upload.id,
            flowCellName: upload.flowCellName,
            path: upload.runFolderPath,
            status: translateUploadResultState(
              upload.status as UploadResultState
            ),
            uploadProgress: upload.progress,
            uploadStartDate: new Date(upload.createdLocalDateTime),
            uploadedBy: upload.owner,
            experimentListName: upload.experimentListName,
            message: upload.message,
            experiment: parseExperiment(upload.experimentJson),
            error: parseErrors(upload.validationErrorJson),
            transmitStarted: upload.transmitStartedUTC
              ? new Date(upload.transmitStartedUTC)
              : undefined,
            transmitEnded: upload.transmitEndedUTC
              ? new Date(upload.transmitEndedUTC)
              : undefined,
            isInFinalStage: upload.isInFinalStage,
          } as IUpload)
      );
    });
};

export type UploadStatusDTO = {
  size: number;
  anyFailed: boolean;
};

export const getUploadStatus: () => Promise<UploadStatusDTO> = async () => {
  return get(routes.api.upload.status).then((response) => response.json());
};

const parseExperiment = (
  value: string | null
): IExperimentResult | undefined => {
  const conf = store.getState().projectPicker.config;

  // conf can be undefined if the "get uploads' request" returns
  // before getting the project metadata. Currently, we are making the
  // "get uploads" request from the Upload component when the conf is
  // not undefined. Therefore, conf should be defined. We may want to
  // introduce an explicit dependency.
  if (conf === undefined || value === null) {
    return undefined;
  }

  switch (conf.name) {
    case 'ClonoSEQ':
      const experiment: Experiment<ITagPlate> = JSON.parse(value);
      return {
        kind: 'mrd',
        tagPlates: experiment.tagPlates,
      };
    case 'ImmunoSEQ':
      const experimentNonMrd: Experiment<TagPlateNonMrd> = JSON.parse(value);
      return {
        kind: 'nonMrd',
        tagPlates: experimentNonMrd.tagPlates,
      };
    default:
      return undefined;
  }
};

export interface IUploadValidationResult {
  success: boolean;
  folderContentErrors: ErrorDetails[];
  experimentErrors: ErrorDetails[];
  results: IExperimentResult;
}

interface IValidationResultTagPlate extends ITagPlate {
  controlSamples: ITagPlateSample[];
}

type UploadValidationResultDTO<T> = {
  folderContentErrors: ErrorDetails[];
  experimentErrors: ErrorDetails[];
  plates: T[];
  success: boolean;
};

export type PlateSampleBaseDTO = {
  name: string;
  wells: { string: string; x: number; y: string }[];
  displayColor: string;
  controlType: 'None' | 'Positive' | 'Negative';
};

export type PlateNonMrdDTO = {
  plateLayout: string;
  kitSN: string;
  samples: PlateSampleBaseDTO[];
  controlSamples: PlateSampleBaseDTO[];
};

const validate = async (
  upload: IRunFolderValidationDto,
  config: BrandConfig
): Promise<IUploadValidationResult> => {
  const form = new FormData();
  if (upload.experimentList) {
    form.append('experimentList', upload.experimentList);
  }

  if (upload.runInfo) {
    form.append('runInfo', upload.runInfo);
  }
  form.append('path', upload.path);
  form.append('files', JSON.stringify(upload.files));
  form.append('fullValidation', upload.fullValidation ? 'true' : 'false');

  const requestInit: RequestInit = {
    body: form,
  };

  if (config.name === 'ImmunoSEQ') {
    return validateNonMrd(requestInit);
  } else {
    return validateMrd(requestInit);
  }
};

function getControlType(controlTypeDTO: string) {
  switch (controlTypeDTO) {
    case 'None':
      return FlowcellControl.none;
    case 'Positive':
      return FlowcellControl.positive;
    case 'Negative':
      return FlowcellControl.negative;
    default:
      return FlowcellControl.none;
  }
}

function validateNonMrd(
  requestInit: RequestInit
): IUploadValidationResult | PromiseLike<IUploadValidationResult> {
  return post(routes.api.upload.validateNonMrd, { init: requestInit })
    .then<UploadValidationResultDTO<PlateNonMrdDTO>>((response) =>
      response.json()
    )
    .then((result) => {
      return {
        folderContentErrors: result.folderContentErrors,
        experimentErrors: result.experimentErrors,
        results: {
          kind: 'nonMrd',
          tagPlates: _.orderBy(
            result.plates.map((plate) => ({
              plateLayout: plate.plateLayout,
              kitSN: plate.kitSN,
              samples: _.orderBy(
                [
                  ...plate.samples.map((sample) => ({
                    ...sample,
                    wells: _.orderBy(sample.wells, ['y']),
                    controlType: getControlType(sample.controlType),
                  })),
                  ...plate.controlSamples.map((sample) => ({
                    ...sample,
                    wells: _.orderBy(sample.wells, ['y']),
                    controlType: getControlType(sample.controlType),
                  })),
                ],
                ['controlType', 'name']
              ),
            })),
            ['kitSN']
          ),
        },
        success: result.success,
      };
    });
}

function validateMrd(
  requestInit: RequestInit
): IUploadValidationResult | PromiseLike<IUploadValidationResult> {
  return post(routes.api.upload.validate, { init: requestInit })
    .then((response: Response) => response.json())
    .then((result) => {
      result.plates.forEach((plate: IValidationResultTagPlate) => {
        // translate control type to enum value
        plate.controlSamples.forEach((sample) => {
          // The type doesn't match the incoming object. FIX.
          const controlTypeDTO = sample.controlType as unknown as string;
          sample.controlType = getControlType(controlTypeDTO);
        });
        plate.samples.forEach((sample) => {
          // The type doesn't match the incoming object. FIX.
          const controlTypeDTO = sample.controlType as unknown as string;
          sample.controlType = getControlType(controlTypeDTO);
        });
        // order the samples by subjectId then name
        plate.samples = _.orderBy(
          plate.samples,
          (sample) => sample.externalSubjectId + sample.name
        );
        // merge samples and control samples into single array
        plate.samples = plate.samples.concat(plate.controlSamples);
        // order the wells for each sample
        plate.samples.forEach(
          (sample) => (sample.wells = _.orderBy(sample.wells, (well) => well.y))
        );
      });
      // Note: The variable `result` is type of `any`. We are also
      // mutating the `result` variable. It might be better to cast it
      // to a DTO and then run the transformation.
      result.results = {
        kind: 'mrd',
        tagPlates: _.orderBy(result.plates, (p) => p.plateLayout),
      };
      return result;
    });
}

export const cancel = async (id: string): Promise<void> => {
  return post(routes.api.upload.cancel.replace(':id', id)).then(() => {});
};

export const remove = async (id: string): Promise<void> => {
  return post(routes.api.upload.remove.replace(':id', id)).then(() => {});
};

export const validateExperimentsFile = (
  file: File,
  config: BrandConfig
): Promise<IUploadValidationResult> => {
  const upload: IRunFolderValidationDto = {
    experimentList: file,
    files: [],
    path: '',
    fullValidation: false,
  };
  return validate(upload, config);
};

interface ICreateUploadDto {
  experimentListName: string;
  experimentListHash: string;
  experimentJson: IExperimentResult;
  experimentList: File;
}

export const createUpload = async (data: ICreateUploadDto): Promise<void> => {
  const form = new FormData();
  form.append('experimentList', data.experimentList);
  form.append('experimentListName', data.experimentListName);
  form.append('experimentListHash', data.experimentListHash);
  form.append('experimentJson', JSON.stringify(data.experimentJson));

  const requestInit: RequestInit = {
    body: form,
  };

  return post(routes.api.upload.create, { init: requestInit }).then(() => {});
};
